import {
  Box,
  Stack,
  Typography,
  TypographyProps,
  useTheme,
} from '@mui/material';
import { useContext } from 'react';

import { COLORS } from '@/styles/tokens/colors';

import { ContextualHelpTooltip } from '../../../modules/content/components/ContextualHelpTooltip';
import {
  FormConfigurationContext,
  FormOptionalDisplayType,
} from '../context/FormConfigurationContext';

type InputLabelProps = React.PropsWithChildren<{
  contextualHelp?: JSX.Element;
  required?: boolean;
  /**
   * hideRequirementIndicator is used for checkboxes and toggles, where there's no need to show an
   * asterisk or "optional"  tag because the default value of the input is already valid
   */
  hideRequirementIndicator?: boolean;
  hidden?: boolean;
  labelIconEnd?: React.ReactNode;
}> &
  React.LabelHTMLAttributes<HTMLLabelElement>;

export function InputLabel({
  children,
  contextualHelp,
  required,
  hidden,
  labelIconEnd,
  hideRequirementIndicator,
  ...props
}: InputLabelProps) {
  // this allows choosing between rendering how to render the requirement status of a given input!
  // the default is "Label name *" for required inputs, and "Label name" for optional inputs.
  // the alternative is e.g. "Label name" for required inputs, and "Label name (optional)" for optional inputs.
  // you can wrap inputs in a FormConfigurationContext
  const formConfigurationContext = useContext(FormConfigurationContext);
  const optionalDisplayType: FormOptionalDisplayType =
    formConfigurationContext?.optionalDisplayType ?? 'required-asterisk';
  const requirementIndicator = (() => {
    if (hideRequirementIndicator) return null;

    switch (optionalDisplayType) {
      case 'optional-label':
        return !required && <span>&nbsp;(optional)</span>;
      case 'required-asterisk':
      // falls through
      default:
        return required && <span>&nbsp;*</span>;
    }
  })();

  return (
    <Stack
      direction="row"
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        minWidth: 0, // allow label to shrink for text truncation
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'flex-start',
          minWidth: 0, // allow label to shrink for text truncation
        }}
      >
        <InputLabelText hidden={hidden} {...props}>
          {children}
          {requirementIndicator}
        </InputLabelText>
        {contextualHelp && !hidden && (
          <ContextualHelpTooltip>{contextualHelp}</ContextualHelpTooltip>
        )}
      </Box>
      {labelIconEnd}
    </Stack>
  );
}

export interface InputLabelTextProps extends Partial<TypographyProps> {
  children: React.ReactNode;
}

export function InputLabelText({ children, ...props }: InputLabelTextProps) {
  const isDarkTheme = useTheme().palette.mode === 'dark';
  return (
    <Typography
      component="label"
      variant="body-xs"
      color={isDarkTheme ? COLORS.NAVY[300] : COLORS.TEXT.PRIMARY}
      sx={{ minWidth: 0 }} // allow label text to shrink for text truncation
      {...props}
    >
      {children}
    </Typography>
  );
}
